import React from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { formatNumber } from '../../Utils/Format';

type PaginationProps = {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPage: (page: number) => void;
  onLimit?: (limit: number) => void;
  limits?: number[];
  loading?: boolean;
};

const pageList = (page: number, totalPages: number): (number | '…')[] => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  if (start > 2) pages.push('…');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push('…');
  pages.push(totalPages);
  return pages;
};

const Pagination: React.FC<PaginationProps> = ({ page, totalPages, total, limit, onPage, onLimit, limits = [25, 50, 100], loading }) => {
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(total, page * limit);
  return (
    <div className="table-footer">
      <div style={{ display: 'flex', alignItems: 'center', gap: '1.2rem' }}>
        <span className="mono">
          {loading && total === 0 ? 'Loading…' : `${formatNumber(from)}–${formatNumber(to)} of ${formatNumber(total)}`}
        </span>
        {onLimit && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', fontWeight: 500 }}>
            Rows
            <select value={limit} onChange={(event) => onLimit(Number(event.target.value))} aria-label="Rows per page">
              {limits.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <nav className="pagination" aria-label="Pagination">
        <button onClick={() => onPage(page - 1)} disabled={page <= 1} aria-label="Previous page">
          <FiChevronLeft />
        </button>
        {pageList(page, Math.max(totalPages, 1)).map((item, index) =>
          item === '…' ? (
            <span key={`e${index}`} className="ellipsis">
              …
            </span>
          ) : (
            <button
              key={item}
              className={item === page ? 'is-active' : ''}
              onClick={() => onPage(item)}
              aria-current={item === page ? 'page' : undefined}
            >
              {formatNumber(item)}
            </button>
          ),
        )}
        <button onClick={() => onPage(page + 1)} disabled={page >= totalPages} aria-label="Next page">
          <FiChevronRight />
        </button>
      </nav>
    </div>
  );
};

export default Pagination;
